import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CalendarDays, ChevronLeft, ChevronRight, ChevronDown, MapPin, Circle, CheckCircle2, ArrowRight, Pencil, Check } from 'lucide-react';
import { toast } from 'sonner';
import { nexaApi } from './api';

const iso = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const today = () => iso(new Date());
const shift = (dateStr, days) => {
  const d = new Date(dateStr + 'T00:00:00');
  d.setDate(d.getDate() + days);
  return iso(d);
};

const fmtHeader = (dateStr) => {
  const d = new Date(dateStr + 'T00:00:00');
  const t = today();
  const rel =
    dateStr === t ? 'Today'
    : dateStr === shift(t, 1) ? 'Tomorrow'
    : dateStr === shift(t, -1) ? 'Yesterday'
    : null;
  const long = d.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' });
  return rel ? `${rel} · ${long}` : long;
};

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const DateJumpPicker = ({ date, onChange }) => {
  const [y, m, d] = date.split('-').map(Number);
  const daysInMonth = new Date(y, m, 0).getDate();
  const thisYear = new Date().getFullYear();
  const years = Array.from({ length: 4 }, (_, i) => thisYear - 1 + i);
  const set = (ny, nm, nd) => {
    const day = Math.min(nd, new Date(ny, nm, 0).getDate());
    onChange(`${ny}-${String(nm).padStart(2, '0')}-${String(day).padStart(2, '0')}`);
  };
  const selectCls =
    'flex-1 h-11 rounded-xl bg-white/[0.05] border border-white/10 text-sm text-white/85 px-2 focus:outline-none focus:border-sky-400/60 appearance-none text-center';
  return (
    <div className="flex gap-2 px-4 py-3" data-testid="nexa-datejump-panel">
      <select
        value={d}
        onChange={(e) => set(y, m, Number(e.target.value))}
        data-testid="nexa-datejump-day"
        aria-label="Day"
        className={selectCls}
      >
        {Array.from({ length: daysInMonth }, (_, i) => i + 1).map((n) => (
          <option key={n} value={n} className="bg-[#0A0A0A]">{n}</option>
        ))}
      </select>
      <select
        value={m}
        onChange={(e) => set(y, Number(e.target.value), d)}
        data-testid="nexa-datejump-month"
        aria-label="Month"
        className={selectCls}
        style={{ flex: 2 }}
      >
        {MONTHS.map((name, i) => (
          <option key={name} value={i + 1} className="bg-[#0A0A0A]">{name}</option>
        ))}
      </select>
      <select
        value={y}
        onChange={(e) => set(Number(e.target.value), m, d)}
        data-testid="nexa-datejump-year"
        aria-label="Year"
        className={selectCls}
      >
        {years.map((n) => (
          <option key={n} value={n} className="bg-[#0A0A0A]">{n}</option>
        ))}
      </select>
    </div>
  );
};

const fmtEventTime = (ev) => {
  if (ev.all_day) return 'All day';
  const s = new Date(ev.start);
  const e = new Date(ev.end);
  const opts = { hour: '2-digit', minute: '2-digit' };
  return `${s.toLocaleTimeString('en-GB', opts)} – ${e.toLocaleTimeString('en-GB', opts)}`;
};

export const CalendarCard = () => {
  const [date, setDate] = useState(today());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pickerOpen, setPickerOpen] = useState(false);

  const load = (d) => {
    setLoading(true);
    nexaApi
      .get('/google/events', { params: { date: d } })
      .then(({ data }) => setData(data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  };

  const toggleTask = async (t) => {
    // Optimistic: mark done, then hide on next fetch (Google filters completed).
    setData((prev) => ({
      ...prev,
      tasks: prev.tasks.map((x) =>
        x.id === t.id ? { ...x, status: 'completed' } : x,
      ),
    }));
    try {
      await nexaApi.patch(`/google/tasks/${encodeURIComponent(t.list_id)}/${encodeURIComponent(t.id)}`, {
        status: 'completed',
      });
      toast.success('Task completed');
      // Refresh a moment later so completed tasks fall off the list.
      setTimeout(() => load(date), 800);
    } catch {
      toast.error('Could not complete task');
      load(date);
    }
  };

  const [editing, setEditing] = useState(null); // {id, list_id, title, due}
  const saveEdit = async () => {
    const e = editing;
    setEditing(null);
    try {
      await nexaApi.put(`/google/tasks/${encodeURIComponent(e.list_id)}/${encodeURIComponent(e.id)}/edit`, {
        title: e.title, due: e.due || '',
      });
      toast.success('Task updated');
      load(date);
    } catch {
      toast.error('Could not update task');
      load(date);
    }
  };

  const snoozeTask = async (t) => {
    // Optimistic: drop from today's list.
    setData((prev) => ({ ...prev, tasks: prev.tasks.filter((x) => x.id !== t.id) }));
    try {
      await nexaApi.post(`/google/tasks/${encodeURIComponent(t.list_id)}/${encodeURIComponent(t.id)}/snooze`, {
        days: 1,
      });
      toast.success('Snoozed to tomorrow');
    } catch {
      toast.error('Could not snooze');
      load(date);
    }
  };

  useEffect(() => {
    load(date);
  }, [date]);

  if (!loading && data && data.connected === false) {
    return null; // don't clutter Today when not connected — Life > Settings has the connect button
  }

  const hasContent = data && (data.events.length > 0 || data.tasks.length > 0);

  return (
    <div data-testid="nexa-calendar-card">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <CalendarDays className="w-3.5 h-3.5 text-sky-400" strokeWidth={1.8} />
          <span className="text-xs tracking-[0.2em] uppercase text-white/40">Schedule</span>
        </div>
        <button
          type="button"
          onClick={() => setPickerOpen((o) => !o)}
          data-testid="nexa-calendar-date"
          aria-expanded={pickerOpen}
          className={`flex items-center gap-1.5 text-[11px] font-mono border rounded-lg px-2.5 py-1.5 transition-colors ${
            pickerOpen ? 'text-sky-400 border-sky-400/60' : 'text-white/70 border-white/10'
          }`}
        >
          {date.split('-').reverse().join('/')}
          <ChevronDown
            className={`w-3 h-3 transition-transform ${pickerOpen ? 'rotate-180' : ''}`}
            strokeWidth={2}
          />
        </button>
      </div>

      <div className="rounded-3xl border border-white/10 bg-[#0A0A0A] overflow-hidden">
        <AnimatePresence initial={false}>
          {pickerOpen && (
            <motion.div
              key="picker"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2, ease: 'easeOut' }}
              className="overflow-hidden border-b border-white/[0.06]"
            >
              <DateJumpPicker date={date} onChange={setDate} />
            </motion.div>
          )}
        </AnimatePresence>
        <div className="flex items-center border-b border-white/[0.06]">
          <button
            onClick={() => setDate((d) => shift(d, -1))}
            data-testid="nexa-calendar-prev"
            aria-label="Previous day"
            type="button"
            className="w-14 h-14 shrink-0 flex items-center justify-center text-white/60 hover:text-sky-400 active:bg-white/[0.04] active:scale-95 transition-all"
          >
            <ChevronLeft className="w-5 h-5" strokeWidth={2} />
          </button>
          <button
            type="button"
            onClick={() => setPickerOpen((o) => !o)}
            data-testid="nexa-calendar-title"
            className="flex-1 text-center py-3"
          >
            <p className="text-sm font-medium">{fmtHeader(date)}</p>
            {date !== today() && (
              <span
                role="button"
                tabIndex={0}
                onClick={(e) => { e.stopPropagation(); setDate(today()); setPickerOpen(false); }}
                data-testid="nexa-calendar-today"
                className="inline-block text-[10px] font-mono tracking-widest text-sky-400/80 hover:text-sky-400 mt-0.5"
              >
                JUMP TO TODAY ↺
              </span>
            )}
          </button>
          <button
            onClick={() => setDate((d) => shift(d, 1))}
            data-testid="nexa-calendar-next"
            aria-label="Next day"
            type="button"
            className="w-14 h-14 shrink-0 flex items-center justify-center text-white/60 hover:text-sky-400 active:bg-white/[0.04] active:scale-95 transition-all"
          >
            <ChevronRight className="w-5 h-5" strokeWidth={2} />
          </button>
        </div>

        {loading && (
          <div className="px-5 py-6 flex items-center justify-center">
            <div className="w-4 h-4 border-2 border-sky-400/30 border-t-sky-400 rounded-full animate-spin" />
          </div>
        )}

        {!loading && !hasContent && (
          <p className="text-sm text-white/40 px-5 py-5">Nothing on this day.</p>
        )}

        {!loading && data && data.events.map((ev, i) => (
          <motion.a
            key={ev.id}
            href={ev.html_link}
            target="_blank"
            rel="noreferrer"
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.03 }}
            className={`block px-5 py-3.5 ${
              (i < data.events.length - 1) || data.tasks.length > 0
                ? 'border-b border-white/[0.06]'
                : ''
            }`}
            data-testid={`nexa-calendar-event-${i}`}
          >
            <div className="flex items-start gap-3">
              <div className="pt-1">
                <div className="w-1.5 h-1.5 rounded-full bg-sky-400" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[11px] font-mono text-sky-400/80 tracking-widest">
                  {fmtEventTime(ev)}
                </p>
                <p className="text-sm text-white/90 mt-0.5">{ev.title}</p>
                {ev.location && (
                  <p className="text-[11px] text-white/40 mt-1 flex items-center gap-1">
                    <MapPin className="w-3 h-3" strokeWidth={1.5} /> {ev.location}
                  </p>
                )}
              </div>
            </div>
          </motion.a>
        ))}

        {!loading && data && data.tasks.map((t, i) => {
          const done = t.status === 'completed';
          const overdue = t.overdue && !done;
          if (editing && editing.id === t.id) {
            return (
              <div key={t.id} className={`px-5 py-3.5 ${i < data.tasks.length - 1 ? 'border-b border-white/[0.06]' : ''}`}
                data-testid={`nexa-task-edit-${i}`}>
                <input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                  data-testid={`nexa-task-edit-title-${i}`}
                  className="w-full bg-white/[0.05] border border-white/10 rounded-lg px-2.5 py-2 text-sm text-white outline-none focus:border-sky-400/50" />
                <div className="flex gap-2 mt-2">
                  <input type="date" value={editing.due} onChange={(e) => setEditing({ ...editing, due: e.target.value })}
                    data-testid={`nexa-task-edit-due-${i}`}
                    className="flex-1 bg-white/[0.05] border border-white/10 rounded-lg px-2.5 py-2 text-sm text-white outline-none focus:border-sky-400/50" />
                  <button onClick={() => setEditing(null)}
                    className="h-9 px-3 rounded-lg border border-white/10 text-white/60 text-[13px]">Cancel</button>
                  <button onClick={saveEdit} data-testid={`nexa-task-edit-save-${i}`}
                    className="h-9 px-3 rounded-lg bg-sky-400 text-black font-semibold text-[13px] flex items-center gap-1">
                    <Check className="w-4 h-4" strokeWidth={2.5} /> Save
                  </button>
                </div>
              </div>
            );
          }
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => !done && toggleTask(t)}
              className={`w-full text-left px-5 py-3.5 flex items-start gap-3 transition-opacity ${
                i < data.tasks.length - 1 ? 'border-b border-white/[0.06]' : ''
              } ${done ? 'opacity-50' : 'active:bg-white/[0.03]'} ${
                overdue ? 'bg-rose-500/[0.05]' : ''
              }`}
              data-testid={`nexa-calendar-task-${i}`}
              aria-pressed={done}
            >
              {done ? (
                <CheckCircle2 className="w-4 h-4 mt-0.5 text-emerald-400 shrink-0" strokeWidth={2} />
              ) : (
                <Circle className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${
                  overdue ? 'text-rose-400' : 'text-amber-400/80'
                }`} strokeWidth={2} />
              )}
              <div className="flex-1 min-w-0">
                <p className={`text-[10px] font-mono uppercase tracking-widest ${
                  overdue ? 'text-rose-400' : 'text-amber-400/70'
                }`}>
                  {overdue
                    ? `Overdue · from ${t.due_day}`
                    : `Task · ${t.list_title}`}
                </p>
                <p className={`text-sm mt-0.5 ${done ? 'text-white/50 line-through' : 'text-white/90'}`}>
                  {t.title}
                </p>
                {t.notes && <p className="text-[11px] text-white/40 mt-1">{t.notes}</p>}
              </div>
              {!done && (
                <span
                  role="button"
                  tabIndex={0}
                  onClick={(e) => { e.stopPropagation(); setEditing({ id: t.id, list_id: t.list_id, title: t.title, due: t.due_day || '' }); }}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); setEditing({ id: t.id, list_id: t.list_id, title: t.title, due: t.due_day || '' }); } }}
                  data-testid={`nexa-calendar-task-edit-${i}`}
                  aria-label="Edit task"
                  className="shrink-0 self-center w-7 h-7 rounded-full border border-white/10 flex items-center justify-center text-white/40 active:scale-95 transition-transform cursor-pointer"
                >
                  <Pencil className="w-3 h-3" strokeWidth={2} />
                </span>
              )}
              {overdue && !done && (
                <span
                  role="button"
                  tabIndex={0}
                  onClick={(e) => { e.stopPropagation(); snoozeTask(t); }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); snoozeTask(t); }
                  }}
                  data-testid={`nexa-calendar-task-snooze-${i}`}
                  aria-label="Snooze to tomorrow"
                  className="shrink-0 self-center flex items-center gap-1 h-7 px-2.5 rounded-full border border-sky-400/45 bg-sky-400/[0.08] text-sky-300 text-[10px] font-mono tracking-widest uppercase active:scale-95 transition-transform cursor-pointer"
                >
                  <ArrowRight className="w-3 h-3" strokeWidth={2.5} />
                  Tomorrow
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};
